import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getUserActivityDetails, updateActivityDetails } from "../../services/user.service";
import moment from "moment";
import {
  Box,
  FormControl,
  InputAdornment,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography
} from "@mui/material";
import { Checkbox, DateRangePicker } from "rsuite";
import { DateRange } from "rsuite/esm/DateRangePicker";
import JiraActivityReportTable from "../../components/JiraActivityReportTable";
import './style.scss'
import { useLocation } from "react-router-dom";
import { PlatformType } from "../../common/model";
import OpenProjectActivityReportTable from "../../components/OpenProjectReportTable";
import CommitsPopup from "./commitsPopup";
import CommentsPopup from "./commentsPopup";
import SearchIcon from "@mui/icons-material/Search";

type UserActivityReportTableProps = {};

const UserActivityReportTable: FC<UserActivityReportTableProps> = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [jiraUserActivity, setJiraUserActivity] = useState([]);
  const [openProjectActivity, setOpenProjectActivity] = useState([])
  const [userNames, setUserNames] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [dateRange, setDateRange] = useState<[Date, Date]>([
    moment().subtract(1, "day").toDate(),
    moment().subtract(1, "day").toDate(),
  ]);
  const { currentUser, users, chartId, userId } = useLocation()?.state || {};
  const [platformSelected, setPlatformSelected] = useState<string>("JIRA")
  const abortControllerRef = useRef<AbortController | null>(null);
  const platformsAvailable = useMemo(() => [{
    displayName: "JIRA",
    selectName: "JIRA"
  },
  {
    displayName: "Waymore Project",
    selectName: "OPEN_PROJECT"
  }
  ],[]);
  const [openDialog, setOpenDialog] = useState(0);
  const [field, setField] = useState('');
  const [issueKey, setIssueKey] = useState('');
  const [selectedDateForPopup, setSelectedDateForPopup] = useState("");
  const [searchText, setSearchText] = useState("");
  const [ticketsOptions, setTicketOptions] = useState([
    { displayOption: "Worked", selectionValue: "assignee" }
  ]);
  const [text, setText] = useState({
    key: "",
    value: 0,
    disabled: false,
    isStarted: false
  });
  const optionsAvailable = useMemo(() => [
    { displayOption: "Worked", selectionValue: "assignee" },
    { displayOption: "Created", selectionValue: "reporter" }
  ], []);

  const handleTicketOptionsChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    if (!value.length || isLoading) return;
  
    setTicketOptions(
      typeof value === "string"
        ? optionsAvailable.filter((option) => value.includes(option.selectionValue))
        : optionsAvailable.filter((option) => value.includes(option.selectionValue))
    );
  };
  
  const handleOpenDialog = async (field: string, issueKey: string, date: string | number, keyName?: string) => {
    if(field === "commits"){
      setOpenDialog(1);
      setField(field);
      setIssueKey(issueKey);
      setSelectedDateForPopup(date as string);
    }else if(field === "comments"){
      setOpenDialog(2);
      setIssueKey(issueKey)
    } else if (field === "priority" && keyName){
      setIssueKey(issueKey)
      setText({
        key: keyName,
        value: date as number,
        disabled: true,
        isStarted: false
      })
    }
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(0);
    setTimeout(() => {
      setField('');
      setIssueKey('');
      setSelectedDateForPopup("");
      setText({
        key: "",
        value: 0,
        disabled: false,
        isStarted: false
      })
    }, 500)
  };
  
  useEffect(() => {
    setUserNames(users ? users : []);
    setSelectedUser(currentUser ? currentUser : "")
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUserChange = (event: SelectChangeEvent<string>) => {
    setSelectedUser(event.target.value);
  };

  const handlePlatformChange = (event: SelectChangeEvent<string>) => {
    setPlatformSelected(event.target.value)
  };

  const handleDateRangeChange = (
    range: DateRange | null
  ) => {
    if (range) {
      setDateRange([range[0], range[1]]);
    }
  };

  const handleTextUpdate = useCallback(async () => {
    if(!text.isStarted) {
      handleCloseDialog()
      return
    }
    setIsLoading(true) 
    try{
      const fields= {
        [text.key]: text.value
      }
      const response = await updateActivityDetails(issueKey.split('/')[0],fields);

      // if(response.statusCode === 200 && response.data){
      //   await handleDownloadReport(
      //     selectedUser ? [selectedUser] : userNames,
      //     moment(dateRange[0]).format("YYYY-MM-DD"),
      //     moment(dateRange[1]).format("YYYY-MM-DD"),
      //     platformSelected,
      //     ticketsOptions.map(option => option.selectionValue)
      //   );
      // }
    } catch (error: any) {
        setJiraUserActivity(jiraUserActivity);
        console.error(error);
    } finally {
      setIsLoading(false);
      handleCloseDialog()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, issueKey, jiraUserActivity, platformSelected, selectedUser, text.key, text.isStarted, text.value, ticketsOptions, userNames])

  const handleTextInputChange = (value: number) => {
    setText(prev => ({ ...prev, value, isStarted: true }));
  }

  const handleDownloadReport = async (
    selectedUser: string[],
    fromDate: string,
    toDate: string,
    platformSelected: string,
    ticketsOptions: string[]
  ) => {
    setIsLoading(true);
    if (!selectedUser.length) {
      setIsLoading(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsLoading(true);
    }

    const newAbortController = new AbortController();
    abortControllerRef.current = newAbortController;
  
    try {
      const userActivityResponse = await getUserActivityDetails(
        selectedUser,
        fromDate,
        toDate,
        platformSelected,
        ticketsOptions,
        chartId, 
        userId,
        users,
        abortControllerRef.current.signal
      );
  
      if (userActivityResponse && userActivityResponse.data) {
        platformSelected === "JIRA" ? setJiraUserActivity(userActivityResponse.data) : setOpenProjectActivity(userActivityResponse.data)
      }
    } catch (error: any) {
      if (error.name === "AbortError") {
        console.error("existing userActivityResponse Request aborted, new userActivityResponse request is in process");
        setIsLoading(true)
      } else {
        setJiraUserActivity([]);
        setOpenProjectActivity([])
        console.error(error);
      }
    } finally {
      if (!abortControllerRef.current.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
        handleDownloadReport(
        selectedUser ? [selectedUser] : userNames,
        moment(dateRange[0]).format("YYYY-MM-DD"),
        moment(dateRange[1]).format("YYYY-MM-DD"),
        platformSelected,
        ticketsOptions.map(option => option.selectionValue)
        );
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [dateRange, selectedUser, userNames, platformSelected, ticketsOptions]);

  const disableFutureDates = (date: Date) => moment(date).isAfter(moment().endOf("day"));

  const renderTableBasedOnPlatform = useCallback(() => {
      switch (platformSelected){
        case PlatformType.JIRA:
          return (
            <JiraActivityReportTable
              isLoading={isLoading}
              userActivity={jiraUserActivity}
              handleOpenDialog={handleOpenDialog}
              handleTextInputChange={handleTextInputChange}
              handleTextUpdate={handleTextUpdate}
              selectedIssueKey={issueKey}
              selectedPrioritizationValue={text.value}
              isInputDisabled={text.disabled}
            />
          );
        case PlatformType.OPEN_PROJECT:
          return <OpenProjectActivityReportTable isLoading={isLoading} userActivity={openProjectActivity} handleOpenDialog={handleOpenDialog}/>
      }
  },[handleTextUpdate, isLoading, issueKey, jiraUserActivity, openProjectActivity, platformSelected, text.disabled, text.value]);

  const containsText = (text: string, searchText: string) =>
    text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;
  const displayedOptions = useMemo(
    () => userNames.filter((option) => containsText(option.userName, searchText)),
    [searchText, userNames]
  );
  
  return (
    <>
    <Box width="98%" height={'92%'} >
      <Box display="flex" alignItems={'center'} justifyContent={'space-between'} marginRight={'5%'}>
        <Box width={'30%'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
          <Typography variant="h4" sx={{fontSize:'xl-large', fontWeight: 500}}>PMS Report</Typography>
        </Box>
        <Box display={'flex'} width={'100%'} alignItems={'center'} gap={'10px'} justifyContent={'flex-end'}>
          <Box width={'20%'} display={'flex'} flexDirection={'column'} alignItems={'flex-start'}>
            <Typography>Options</Typography>
            <FormControl fullWidth>
            <Select
              multiple
              value={ticketsOptions.map((option) => option.selectionValue)}
              onChange={handleTicketOptionsChange}
              renderValue={(selected) =>
                ticketsOptions
                  .filter((option) => selected.includes(option.selectionValue))
                  .map((option) => option.displayOption)
                  .join(", ")
              }
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    overflowY: "auto",
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                  },
                },
              }}
            >
              {optionsAvailable.map((option) => (
                <MenuItem key={option.selectionValue} value={option.selectionValue}>
                  <Checkbox checked={ticketsOptions.some((o) => o.selectionValue === option.selectionValue)} />
                  <ListItemText primary={option.displayOption} />
                </MenuItem>
              ))}
            </Select>
            </FormControl>
          </Box>
          <Box display={'flex'} alignItems={'flex-start'} flexDirection={'column'}>
            <Typography>Date</Typography>
            <DateRangePicker
            className="date-picker-large"
            value={dateRange}
            onChange={handleDateRangeChange}
            format="yyyy-MM-dd"
            shouldDisableDate={disableFutureDates}
            editable={false}
          />
          </Box>
          <Box width={'20%'} display={'flex'} flexDirection={'column'} alignItems={'flex-start'}>
            <Typography>Platform</Typography>
            <FormControl fullWidth>
              <Select value={platformSelected} onChange={handlePlatformChange} sx={{height:'80%'}} MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                  },
                },
              }}>
                {platformsAvailable.map((platform, index) => (
                  <MenuItem key={index} value={platform.selectName}>{platform.displayName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box width={'20%'} display={'flex'} flexDirection={'column'} alignItems={'flex-start'}>
            <Typography>Users</Typography>
            <FormControl fullWidth>
              <Select
                value={selectedUser}
                onChange={handleUserChange}
                sx={{ height: '80%' }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      overflowY: 'auto',
                      '&::-webkit-scrollbar': {
                        display: 'none',
                      },
                    },
                  },
                  autoFocus: false
                }}
                onClose={() => setSearchText("")}
              >
                <ListSubheader>
                  <TextField
                    size="small"
                    autoFocus
                    placeholder="Type to search..."
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== "Escape") {
                        e.stopPropagation();
                      }
                    }}
                  />
                </ListSubheader>
                {displayedOptions.map((user: any) => (
                    <MenuItem key={user.user} value={user.user}>{user.userName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {renderTableBasedOnPlatform()}
    </Box>
    <CommitsPopup 
      open={openDialog === 1}
      field={field}
      fromDate={moment(dateRange[0]).format("YYYY-MM-DD")} 
      toDate={moment(dateRange[1]).format("YYYY-MM-DD")}
      issueKey={[issueKey]}
      user={selectedUser}
      platform={platformSelected}
      handleClose={handleCloseDialog}
      selectedDateForPopup={selectedDateForPopup}
    />
    <CommentsPopup 
      open={openDialog === 2}
      issueKey={[issueKey]}
      handleClose={handleCloseDialog}
      platform={platformSelected}
    />
    </>
  );
};

export default UserActivityReportTable;
